import { pushToDataLayer } from '../dataLayer'
import { slugify } from '../utils'
import type {
  TKindActionMenu,
  TKindAlertCard,
  TKindExportMenu,
  TKindFilter,
  TKindGrid,
  TKindInventory,
  IActionBulk,
  IFilterSearch,
  TKindImportSheetMenu,
  TKindConfirmModal,
  IEditProduct,
  TQueueAction,
  TQueueOrigin,
  TIndicatorKey,
} from './types'

const clickEventAlertCard = (kind: TKindAlertCard): void => {
  const getKind = {
    LOSING_COMPETITIVENESS: 'perdendo-competitividade',
    WITHOUT_STOCK: 'sem-estoque',
    LOW_RELEVANCE: 'baixa-relevancia',
    LOW_STOCK: 'estoque-baixo',
    PROMOTIONAL_PRICE: 'em-promocao',
    REPROVED: 'aguardando-correcao',
  }

  if (!getKind[kind]) return

  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos',
    content_id: getKind[kind],
  })
}

const clickEventExport = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:catalogo:opcoes',
    eventLabel: 'baixar-meu-catalogo-como-planilha',
  })
}

const clickEventExportMenu = (kind: TKindExportMenu): void => {
  const getKind = {
    ALL: 'todos-os-produtos',
    CANCEL: 'cancelar',
    PAGE_CURRENT: 'pagina-atual',
    SELECTED: 'selecionado',
  }

  pushToDataLayer({
    eventAction: 'download',
    eventCategory: 'produto:catalogo:opcoes',
    eventLabel: `baixar-meu-catalogo-como-planilha:${getKind[kind]}`,
  })
}

const clickEventImport = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:catalogo:opcoes',
    eventLabel: 'subir-planilha-para-meu-catalogo',
  })
}

const clickEventNewProduct = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:catalogo:opcoes',
    eventLabel: 'cadastrar-produto',
  })
}

const clickEventViewGrid = (kind: TKindGrid): void => {
  const getKind = {
    VIEW_GROUPED: 'agrupar-variacoes',
    VIEW_UNGROUPED: 'desagrupar-variacoes',
  }

  pushToDataLayer({
    eventCategory: 'produto:catalogo:visualizacao',
    eventLabel: getKind[kind],
  })
}

const clickEventViewFilter = (kind: TKindFilter[]): void => {
  const getKind = {
    NEW: 'novos',
    PUBLISHED: 'publicados',
    UNPUBLISHED: 'despublicados',
    BLOCKED: 'bloqueados',
    INACTIVE: 'inativos',
    MAGALU_INDICA_ACTIVE: 'magalu-indica',
  }

  const label = kind.map(item => getKind[item]).join('|')

  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos',
    content_id: `menu:${label}`,
  })
}

const clickEventIconInventory = (kind: TKindInventory): void => {
  const getKind = {
    RESERVED: 'estoque-reserva',
    FULFILLMENT: 'estoque-fulfillment',
  }

  pushToDataLayer({
    eventCategory: 'produto:catalogo:lista',
    eventLabel: `icone:${getKind[kind]}`,
  })
}

const clickEventActionMenu = (kind: TKindActionMenu): void => {
  const getKind = {
    NEW_SIMILAR: 'duplicar',
    LINK_EDIT: 'editar',
    LINK_MARKETPLACE: 'abrir-no-site',
    PUBLISH: 'publicar',
    UNPUBLISH: 'despublicar',
    VIEW_HISTORY_TIMELINE: 'historico',
    INACTIVE: 'inativar',
    PERFORMANCE: 'ver-desempenho',
  }

  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: getKind[kind],
  })
}

const clickEventActionBulk = (props: IActionBulk): void => {
  const { action, kind, quantity } = props

  const getActionButton = {
    CANCEL: 'cancelar',
    SAVE: 'salvar',
  }

  const getKindOptionMenu = {
    PUBLISH: 'publicar-produto',
    UNPUBLISH: 'despublicar-produto',
    EDIT_PRICE: 'editar-preco',
    EDIT_INVENTORY: 'editar-estoque',
  }

  const label = `${getActionButton[action]}:${getKindOptionMenu[kind]}:${quantity}`

  pushToDataLayer({
    eventCategory: 'produto:catalogo:acoes-em-massa',
    eventLabel: `editar:${label}`,
  })
}

const clickEventFilterSearch = (props: IFilterSearch): void => {
  const { options, query } = props

  pushToDataLayer({
    eventCategory: 'produto:catalogo:busca',
    eventLabel: `${options}:${query}`,
  })
}

const clickEventPaginate = (kind: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:catalogo:lista',
    eventLabel: `paginacao:${kind}`,
  })
}

const clickEventPromotion = (
  kind: 'abrir-listagem' | 'sim-editar-preco' | 'nao-editar-preco'
): void => {
  pushToDataLayer({
    eventCategory: 'produto:promocao',
    eventLabel: kind,
  })
}

const clickEventFastUpdate = (quantity: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:catalogo:edicao-rapida',
    eventLabel: `salvar-alteracoes:${quantity}`,
  })
}

const clickEventCancelFastUpdate = (quantity: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:catalogo:edicao-rapida',
    eventLabel: `cancelar:${quantity}`,
  })
}

const clickEventImportSheetOptions = (option: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:importar-planilha',
    eventLabel: `importar-planilha:${option}`,
  })
}

const clickEventDownloadSheet = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:importar-planilha',
    eventLabel: 'baixar-planilha-modelo',
  })
}

const clickEventImportSheet = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:importar-planilha',
    eventLabel: 'importar-planilha',
  })
}

const clickEventImportNewSheet = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:historico-de-importacoes',
    eventLabel: 'importar-nova-planilha',
  })
}

const clickEventImportSheetMenu = (kind: TKindImportSheetMenu): void => {
  const getKind = {
    CANCEL: 'cancelar',
    CONFIRM: 'confirmar',
    DOWNLOAD: 'fazer-download',
  }

  pushToDataLayer({
    eventCategory: 'produto:historico-de-importacoes',
    eventLabel: `opcao:${getKind[kind]}`,
  })
}

const clickEventImportSheetFailure = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:historico-de-importacoes',
    eventLabel: 'falhas-de-importacao',
  })
}

const clickEventSearchProduct = (key: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:cadastrar:busca',
    eventLabel: key,
  })
}

const clickEventImportSheetFailureTabs = (option: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:falhas-de-importacao',
    eventLabel: `guia:${option}`,
  })
}

const clickEventCreateProduct = (sku: string, variations: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:novo',
    eventLabel: `cadastrar:${sku}:variacoes:${variations}`,
  })
}

const clickEventNewImportLink = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:cadastrar',
    eventLabel: 'importar-planilha',
  })
}

const clickEventOpenCostSimulator = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'calculadora-de-custos',
  })
}

const clickEventUpdatePriceOffer = (): void => {
  pushToDataLayer({
    eventCategory: 'simular-custos:atualizou-preco',
    eventLabel: 'atualizou-preco',
  })
}

const clickEventRegisterNewProduct = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:cadastrar',
    eventLabel: 'cadastrar-novo-produto',
  })
}

const clickEventRegisterNewWithMatch = (option: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:cadastrar',
    eventLabel: `vender-este:${option}`,
  })
}

const clickEventModalDiscardChanges = (kind: TKindConfirmModal): void => {
  const getKind = {
    CANCEL: 'cancelar',
    CONFIRM: 'confirmar',
  }

  pushToDataLayer({
    eventCategory: 'produto:editar',
    eventLabel: `descarta-alteracoes:${getKind[kind]}`,
  })
}

const clickEventEditProduct = (props: IEditProduct): void => {
  const { sku, quantity } = props

  pushToDataLayer({
    eventCategory: 'produto:editar',
    eventLabel: `atualizar:${sku}:variacoes:${quantity}`,
  })
}

const clickEventDiscardEditProduct = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:editar',
    eventLabel: 'descarta-alteracoes',
  })
}

const clickEventBackEditProduct = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:editar',
    eventLabel: 'voltar',
  })
}

const clickEventModalPublishBlockedProduct = (kind: TKindConfirmModal): void => {
  const getKind = {
    CANCEL: 'cancelar',
    CONFIRM: 'confirmar',
  }

  pushToDataLayer({
    eventCategory: 'produto:consentimento-blocklist',
    eventLabel: `opcao:${getKind[kind]}`,
  })
}

const clickEventSortByPortfolio = (sort: string): void => {
  pushToDataLayer({
    eventCategory: 'produto:catalogo:visualizacao',
    eventLabel: `ordenar:${slugify(sort)}`,
  })
}

const clickEventIconBlocklist = (): void => {
  pushToDataLayer({
    eventCategory: 'produto:catalogo:lista',
    eventLabel: 'icone:blocklist',
  })
}

const clickEventIconQueueActions = (origin: TQueueOrigin, action: TQueueAction): void => {
  pushToDataLayer({
    eventCategory: `produto:catalogo:fila-atualizacao:${origin}`,
    eventLabel: action,
  })
}

const clickEventIconQueueDetails = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'ver-detalhes',
  })
}

const clickEventPopoverPreferences = (kind: 'SHOW_INACTIVE' | 'HIDDEN_INACTIVE'): void => {
  const getKind = {
    SHOW_INACTIVE: 'ocultar-ativo',
    HIDDEN_INACTIVE: 'ocultar-inativo',
  }

  pushToDataLayer({
    eventCategory: 'produto:catalogo:preferencias',
    eventLabel: `opcao:${getKind[kind]}`,
  })
}

const clickEventDialogInactiveConfirm = (kind: 'CANCEL' | 'CONFIRM'): void => {
  const getKind = {
    CANCEL: 'cancelar',
    CONFIRM: 'confirmar',
  }

  pushToDataLayer({
    eventCategory: 'produto:catalogo:dialogo-inativar',
    eventLabel: `opcao:${getKind[kind]}`,
  })
}

const clickEventDialogActiveConfirm = (kind: 'CANCEL' | 'CONFIRM'): void => {
  const getKind = {
    CANCEL: 'cancelar',
    CONFIRM: 'confirmar',
  }

  pushToDataLayer({
    eventCategory: 'produto:catalogo:dialogo-ativar',
    eventLabel: `opcao:${getKind[kind]}`,
  })
}

const clickEventIncreaseRelevance = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'relevancia-do-conteudo:melhor-conteudo',
  })
}

const clickEventUpdateRelevance = (): void => {
  pushToDataLayer({
    eventCategory: 'listagem:tela-relevancia',
    eventLabel: 'concluir',
  })
}

const clickEventSimulateFreight = (): void => {
  pushToDataLayer({
    eventCategory: 'simulador-de-frete',
    eventLabel: 'consulta',
  })
}

const clickEventSelectSellOrView = (kind: 'visits' | 'sales'): void => {
  const getKind = {
    visits: 'aba-contador-visitas',
    sales: 'aba-contador-vendas',
  }

  pushToDataLayer({
    eventCategory: 'produto:catalogo:visualizacao',
    eventLabel: `listagem:${getKind[kind]}`,
  })
}

const clickEventProductVariationsMenu = (): void => {
  pushToDataLayer({
    eventCategory: 'desempenho-produto',
    eventLabel: 'componente-variacoes',
  })
}

const clickEventPeriodFilter = (days: number): void => {
  const eventLabel = days === 1 ? 'filtro-ontem' : `filtro-${days}-dias`

  pushToDataLayer({
    eventCategory: 'desempenho-produto',
    eventLabel,
  })
}

const clickEventIndicatorCard = (indicatorKey: TIndicatorKey): void => {
  const indicatorLabel = {
    visit_qty: 'visitas',
    sales_total: 'vendas',
    conversion_rate: 'conversao',
    completed_orders: 'pedidos',
    items_sold: 'itens-vendidos',
    cancelled_orders: 'pedidos-cancelados',
  }[indicatorKey]

  pushToDataLayer({
    eventCategory: 'desempenho-produto',
    eventLabel: `card-${indicatorLabel}`,
  })
}

const clickEventApiUpdateBlock = (isBlocked: boolean): void => {
  const selectedEvent = isBlocked
    ? 'atualizacao-produto-via-integradora:bloqueado'
    : 'atualizacao-produto-via-integradora:habilitado'
  pushToDataLayer({
    eventCategory: 'produto:catalogo',
    eventLabel: selectedEvent,
  })
}

const clickEventSidebarMenuItem = (menuItem: string): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos',
    content_id: `menu:${slugify(menuItem)}`,
  })
}

const clickEventMagaluAds = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'desempenho:impulsione-suas-vendas-com-magalu-ads',
  })
}

const clickEventProductPerformance = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'desempenho:ver-mais',
  })
}

const clickEventFilterDropdownSearch = (filter: string): void => {
  pushToDataLayer({
    event: 'search',
    content_type: 'portal-seller:catalogo-produtos',
    content_id: slugify(filter),
  })
}

const clickEventChangeProductPrice = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'preco:alterar',
  })
}

const clickEventSetLowestPlatformPrice = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'alterar-preco:aplicar-menor-preco-da-plataforma',
  })
}

const clickEventSaveUpdatedProductPrice = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'alterar-preco:salvar',
  })
}

const clickEventOpenPromotionsDialog = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'preco:promocao-ativa',
  })
}

const clickEventChangeProductInventory = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'estoque:alterar',
  })
}

const clickEventSaveProductInventory = (): void => {
  pushToDataLayer({
    event: 'select_content',
    content_type: 'portal-seller:catalogo-produtos:produto',
    content_id: 'estoque:salvar',
  })
}

export {
  clickEventAlertCard,
  clickEventExport,
  clickEventExportMenu,
  clickEventImport,
  clickEventNewProduct,
  clickEventViewGrid,
  clickEventViewFilter,
  clickEventIconInventory,
  clickEventActionMenu,
  clickEventActionBulk,
  clickEventFilterSearch,
  clickEventPaginate,
  clickEventPromotion,
  clickEventFastUpdate,
  clickEventCancelFastUpdate,
  clickEventImportSheetOptions,
  clickEventDownloadSheet,
  clickEventImportSheet,
  clickEventImportNewSheet,
  clickEventImportSheetMenu,
  clickEventImportSheetFailure,
  clickEventSearchProduct,
  clickEventImportSheetFailureTabs,
  clickEventCreateProduct,
  clickEventNewImportLink,
  clickEventRegisterNewProduct,
  clickEventRegisterNewWithMatch,
  clickEventModalDiscardChanges,
  clickEventEditProduct,
  clickEventDiscardEditProduct,
  clickEventBackEditProduct,
  clickEventModalPublishBlockedProduct,
  clickEventSortByPortfolio,
  clickEventIconBlocklist,
  clickEventIconQueueActions,
  clickEventIconQueueDetails,
  clickEventPopoverPreferences,
  clickEventDialogInactiveConfirm,
  clickEventDialogActiveConfirm,
  clickEventIncreaseRelevance,
  clickEventUpdateRelevance,
  clickEventSelectSellOrView,
  clickEventSimulateFreight,
  clickEventOpenCostSimulator,
  clickEventUpdatePriceOffer,
  clickEventProductVariationsMenu,
  clickEventPeriodFilter,
  clickEventIndicatorCard,
  clickEventApiUpdateBlock,
  clickEventSidebarMenuItem,
  clickEventMagaluAds,
  clickEventProductPerformance,
  clickEventFilterDropdownSearch,
  clickEventChangeProductPrice,
  clickEventSetLowestPlatformPrice,
  clickEventSaveUpdatedProductPrice,
  clickEventOpenPromotionsDialog,
  clickEventChangeProductInventory,
  clickEventSaveProductInventory,
}
